import { Circ, Expo, Linear, RoughEase, TimelineLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import { getTexture } from "html-living-framework/lib/Utils/PixiUtils";
import { Emitter } from "pixi-particles";
import * as PIXI from "pixi.js";
import { Application } from "../Application";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import LightsOutGameModel from "../data/LightsOutGameModel";
import { LightsOutObjectType } from "../data/LightsOutObjectType";
import * as VO from "../data/VO";
import { AnimatedLightsOutObject } from "../gameobjects/AnimatedLightsOutObject";
import { Button } from "../gameobjects/Button";
import { LightsOutObject } from "../gameobjects/LightsOutObject";
import { Radio } from "../gameobjects/Radio";
import { StaticLightsOutObject } from "../gameobjects/StaticLightsOutObject";
import {LightsOutElmoBathroomLayout} from "../layout/LightsOutElmoBathroomLayout";
import {AbstractGameState} from "./AbstractGameState";
import { AbstractLightsOutGameState } from "./AbstractLightsOutGameState";

export class LightsOutElmoBathroomScreen extends AbstractLightsOutGameState {

    public static readonly NAME = "LightsOutElmoBathroomScreen";

    public readonly bg = this.addChild(new Sprite());
    public readonly lightswitch = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Light}));
    public readonly ducky = this.addChild(new Sprite("ducky"));
    public readonly shelf = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.EasterEgg}));
    public readonly toothbrush = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Electricity}));
    public readonly sink = this.addChild(new AnimatedLightsOutObject({
        type: LightsOutObjectType.Water,
        spriteSheet: "lightsout-bathroom-1",
        frames: 7,
    }));
    public readonly soap = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.EasterEgg}));
    public readonly lights = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Light}));
    public readonly picture = this.addChild(new AnimatedLightsOutObject({
        type: LightsOutObjectType.EasterEgg,
        spriteSheet: "lightsout-bathroom-1",
        frames: 8,
    }));
    public readonly stool = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.EasterEgg}));
    public readonly nightlight = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Light}));
    public readonly glowtoy = this.addChild(new AnimatedLightsOutObject({
        type: LightsOutObjectType.Electricity,
        spriteSheet: "lightsout-bathroom-1",
        frames: 10,
    }));

    public readonly radio = this.addChild(new Radio());
    public readonly bubblesContainer = this.addChild(new PIXI.Container());
    public readonly nightlightGlow = this.addChild(new Sprite());
    public readonly lightsGlow = this.addChild(new Sprite());

    public bubbles = new Emitter(this.bubblesContainer, getTexture("bubble", "lightsout-bathroom-1"), {
        alpha: {
            start: 0.5,
            end: 1,
        },
        scale: {
            start: 0.1,
            end: 1.,
            minimumScaleMultiplier: 0.75,
        },
        color: {
            start: "#ffffff",
            end: "#ffffff",
        },
        speed: {
            start: 100,
            end: -10,
            minimumSpeedMultiplier: 1,
        },
        acceleration: {
            x: 0,
            y: 0,
        },
        maxSpeed: 0,
        startRotation: {
            min: -50,
            max: -140,
        },
        // noRotation: true,
        rotationSpeed: {
            min: -10,
            max: 10,
        },
        lifetime: {
            min: 1,
            max: 1.5,
        },
        blendMode: "normal",
        frequency: 0.15,
        emitterLifetime: -1,
        maxParticles: 200,
        pos: {
            x: 0,
            y: 0,
        },
        addAtBack: true,
        spawnType: "circle",
        spawnCircle: {
            x: 0,
            y: 0,
            r: 30,
        },
    });
    protected analyticsScreenName: string = "5b Lights Out_Bathroom Screen";

    protected readonly roomID = "Bathroom";
    protected readonly roomNameVO = VO.LO_ROOM04;

    protected _subscriptions: any[] = [];

    protected itemsToTurnOff: LightsOutObject[] = [
        this.lightswitch,
        this.toothbrush,
        this.sink,
        this.radio,
        this.nightlight,
        this.glowtoy,
    ];

    public init(pSize: PIXI.Point, pData: {}) {
        LightsOutElmoBathroomLayout.call(this);
        super.init(pSize, pData);

        this.sink.hoverSFX = "lights-out-tap-drip";
        this.toothbrush.hoverSFX = "lights-out-toothbrush";
        this.lights.lightsOutSFX = "lights-out-light-switch";
        this.lightswitch.lightsOutSFX = "lights-out-light-switch";
        this.nightlight.lightsOutSFX = "lights-out-electronics-button";
        this.radio.lightsOutSFX = "lights-out-electronics-button";
        this.glowtoy.lightsOutSFX = "lights-out-teddy-squeak";
        this.shelf.clickSfx = "lights-out-rubber-duck";
        this.picture.clickSfx = "lights-out-fish";
        this.soap.clickSfx = "lights-out-soap-bubbles";

        this.makeObjectClickable(this.toothbrush);
        this.makeObjectClickable(this.sink);
        this.makeObjectClickable(this.radio);
        this.makeObjectClickable(this.nightlight);
        this.makeObjectClickable(this.glowtoy);
        this.makeObjectClickable(this.picture);
        this.makeObjectClickable(this.shelf);
        this.makeSwitch(this.lightswitch, this.lights);

        this.bubblesContainer.position.copyFrom(this.soap.position);
        this.bubbles.emit = false;
        this.soap.onDown.add(() => {
            this.bubbles.emitterLifetime = 3;
            this.bubbles.emit = true;
        });

        this.ducky.position.copyFrom(this.shelf.position); // temp

        // randomize objects
        HLF.ArrayUtils.shuffle(this.itemsToTurnOff);

        // move already played objects to end of array
        this.itemsToTurnOff.sort((a, b) => LightsOutGameModel.rooms.Bathroom.itemsLeftOn.indexOf(a.name));

        // offset the array by one so it's not just flip flopping between two sets of three
        this.itemsToTurnOff.push(this.itemsToTurnOff.shift()!);

        // bring the sink to the beginning of the array if we have not yet encountered a water type item this playthrough
        if (LightsOutGameModel.rooms.Kitchen.completed && LightsOutGameModel.rooms.Kitchen.itemsLeftOn.length) {
            // if previously played kitchen
            // but faucet was not one of the items left on
            if (LightsOutGameModel.rooms.Kitchen.itemsLeftOn.indexOf("faucet") < 0) {
                console.log ("must leave the bathroom sink on because kitchen faucet was not left on this round");
                this.itemsToTurnOff.sort((a, b) => a === this.sink ? -1 : 1);
            }
            else {
                // offset the array by one so it's not just flip flopping between two sets of three
                this.itemsToTurnOff.unshift(this.itemsToTurnOff.pop()!);
            }
        }
        else {
            console.log ("must leave bathroom sink on because kitchen has not been played yet this round");
            this.itemsToTurnOff.sort((a, b) => a === this.sink ? -1 : 1);
        }

        LightsOutGameModel.rooms.Bathroom.itemsLeftOn = [];

        // disable all but three of them
        for (let i =  0; i < this.itemsToTurnOff.length; i++) {
            if (i < 3) {
                this.itemsToTurnOff[i].TurnOn();
                this.itemsToTurnOff[i].Highlight();
                LightsOutGameModel.rooms.Bathroom.itemsLeftOn.push(this.itemsToTurnOff[i].name);
            }
            else {
                this.itemsToTurnOff[i].TurnOn({animate: false}); // hacky fix for lights glow
                this.itemsToTurnOff[i].TurnOff({animate: false});
            }
        }

        HLF.PixiUtils.sendToFront(this.homeButton);
        HLF.PixiUtils.sendToFront(this.elmo);
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.position.set((pSize.x - 1664) / 2, 0);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        this.bubbles.update(pDeltaTime);
    }

    public destroy() {
        this.bubbles.autoUpdate = false;
        this.bubbles.cleanup();
        super.destroy();
        for (const subscription of this._subscriptions) {
            PubSub.unsubscribe(subscription);
        }
    }

    protected onItemTurnedOn(pTopic: "item turned on", pItem: LightsOutObject) {
        super.onItemTurnedOn(pTopic, pItem);
        if (pItem === this.shelf) {
            TweenMax.fromTo(this.ducky, 0.5, {pixi: {x: this.shelf.x}}, {pixi: {x: this.shelf.x - 80}}).yoyo(true).repeat(1).repeatDelay(0.5);
        }
        else if (pItem === this.toothbrush) {
            TweenMax.fromTo(this.toothbrush.sprite, 0.1, {pixi: {x: -2}, ease: Linear.easeNone}, {pixi: {x: 2}}).yoyo(true).repeat(-1).repeatDelay(0.25);
        }
    }

    protected onItemTurnedOff(pTopic: "item turned off", pItem: LightsOutObject) {
        super.onItemTurnedOff(pTopic, pItem);
        if (pItem === this.toothbrush) {
            TweenMax.killTweensOf(this.toothbrush.sprite);
        }
    }
}
